<script lang="ts" setup>
import { isEqual } from 'lodash-es';
import { computed, inject } from 'vue';

import {
  ButtonRadioEmits,
  ButtonRadioProps,
} from 'lib/components/buttonradio/ButtonRadio.vue.d';
import Label from 'lib/components/helpers/label';

import Icon from '../icon/Icon.vue';

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<ButtonRadioProps>(), {
  tooltipPos: 'top',
  binary: false,
});

const emit = defineEmits<ButtonRadioEmits>();
const Preset = inject<Record<string, any>>('preset', {}).buttonradio;

const checked = computed(() => {
  return (
    props.modelValue != null &&
    (props.binary ? isBinaryEqual() : isEqual(props.modelValue, props.value))
  );
});

const isBinaryEqual = (): boolean => {
  const { modelValue, value } = props;
  if (typeof modelValue != 'boolean') return !!modelValue;
  if (typeof value === 'boolean') {
    return modelValue === value;
  } else if (typeof value === 'number') {
    return !!modelValue === !!value;
  } else if (typeof value === 'string') {
    return modelValue?.toString() === value;
  }

  return false;
};

const binaryValue = (value: any): boolean => {
  if (typeof value === 'boolean') {
    return value;
  } else if (typeof value === 'number') {
    return !!value;
  } else if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
};

const onChange = (event: Event): void => {
  if (!props.disabled && !props.readonly) {
    const newModelValue = props.binary ? binaryValue(props.value) : props.value;
    emit('update:modelValue', newModelValue);
    emit('change', event);
  }
};

const onFocus = (event: Event): void => {
  emit('focus', event);
};

const onBlur = (event: Event): void => {
  emit('blur', event);
};
</script>

<template>
  <Label v-bind="Preset.label" :label="label">
    <span v-bind="Preset.root({ props, state: { checked } })">
      <input
        :id="inputId"
        :aria-invalid="invalid || undefined"
        :checked="checked"
        :class="[Preset.input({ props }).class, inputClass]"
        :disabled="disabled"
        :name="name"
        :readonly="readonly"
        :style="inputStyle"
        :tabindex="tabindex"
        :value="value"
        @blur="onBlur"
        @change="onChange"
        @focus="onFocus"
        type="radio"
      />

      <span v-bind="Preset.box({ props, state: { checked } })">
        <span v-bind="Preset.icon" />
      </span>
    </span>

    <slot name="label">
      <span v-if="label" data-wv-section="labeltext" v-bind="Preset.labeltext">
        {{ label }}
      </span>
    </slot>

    <Icon
      v-if="tooltip"
      :info="tooltip"
      :tooltip-pos="tooltipPos"
      v-bind="Preset.tooltipicon"
      data-wv-section="tooltipicon"
      icon="info"
    />
  </Label>
</template>
